import {
    CLOSED_BILL_STATUSES,
    CLOSED_INVOICE_STATUSES,
    getBillEffectiveStatus,
    getInvoiceEffectiveStatus,
    isImporterBillSource,
} from "../accrual-source";
import { billLineContribution } from "../lineItems";
import {
    fromMonetaryToInt,
    subtractMonetaryValues,
    sumMonetaryValues,
} from "../monetary";
import { BillLineItemKind, BillSource, BillStatus } from "../../types/bill";
import { InvoiceSource, InvoiceStatus } from "../../types/invoice";

interface BillOpenAmountInput {
    entityId: number;
    total: number;
    source: BillSource;
    status: BillStatus;
    externalStatus: BillStatus | null;
    lines?: Array<{ matchedAmount: number; kind?: BillLineItemKind | null }>;
}

interface BillLineOpenAmountInput {
    amount: number;
    matchedAmount: number;
    kind?: BillLineItemKind | null;
    bill?: {
        entityId: number;
        source: BillSource;
        status: BillStatus;
        externalStatus: BillStatus | null;
    };
}

interface InvoiceOpenAmountInput {
    entityId: number;
    total: number;
    source: InvoiceSource;
    status: InvoiceStatus;
    externalStatus: InvoiceStatus | null;
    lines?: Array<{ matchedAmount: number }>;
}

interface InvoiceLineOpenAmountInput {
    amount: number;
    matchedAmount: number;
    invoice?: {
        entityId: number;
        source: InvoiceSource;
        status: InvoiceStatus;
        externalStatus: InvoiceStatus | null;
    };
}

/**
 * Open amount math depends on whether the entity has an accrual ledger:
 * - With accrual ledger: derive from accrual matches via `matchedAmount`;
 *   PAID/VOID reads 0 even with no matches, since a document closed before
 *   migration (e.g. paid in QuickBooks) never gets a Kick match.
 * - Without accrual ledger: use the effective status (internal-source records
 *   resolve to `status`, integration records to `externalStatus`), matching
 *   the by-counterparty SQL aggregation. An integration record with a NULL
 *   `externalStatus` yields 0, since its provider state is unknown.
 */

function isClosedBillStatus(status: BillStatus): boolean {
    return CLOSED_BILL_STATUSES.includes(status);
}

function isClosedInvoiceStatus(status: InvoiceStatus): boolean {
    return CLOSED_INVOICE_STATUSES.includes(status);
}

export function getBillMatchedAmount(
    bill: Pick<BillOpenAmountInput, "source" | "lines">,
): number {
    return (
        bill.lines?.reduce(
            (sum, line) =>
                sum +
                (isImporterBillSource(bill.source)
                    ? billLineContribution({
                          amount: line.matchedAmount,
                          kind: line.kind,
                      })
                    : line.matchedAmount),
            0,
        ) ?? 0
    );
}

export function getBillOpenAmount(
    bill: BillOpenAmountInput,
    accrualEntityIds: Set<number>,
): number {
    if (accrualEntityIds.has(bill.entityId)) {
        if (isClosedBillStatus(bill.status)) {
            return 0;
        }
        return bill.total - getBillMatchedAmount(bill);
    }
    return getBillEffectiveStatus(bill) === BillStatus.OPEN ? bill.total : 0;
}

export function getBillLineOpenAmount(
    line: BillLineOpenAmountInput,
    accrualEntityIds: Set<number>,
): number {
    const bill = line.bill;
    if (!bill) {
        return 0;
    }
    if (accrualEntityIds.has(bill.entityId)) {
        if (isClosedBillStatus(bill.status)) {
            return 0;
        }
        return billLineContribution({
            kind: line.kind,
            amount: line.amount - line.matchedAmount,
        });
    }
    return getBillEffectiveStatus(bill) === BillStatus.OPEN
        ? billLineContribution(line)
        : 0;
}

interface FullyMatchedDocumentInput<TLine> {
    total: number;
    lines?: TLine[];
    isMatchableLine: (line: TLine) => boolean;
    isDocumentDiscountLine: (line: TLine) => boolean;
    shouldSubtractDocumentAdjustmentMatches?: boolean;
}

// A discount or deposit row reduces the total, so a discounted document is
// paid once matched lines, net of any matched discount or deposit, reach it.
export function isAccrualDocumentFullyMatched<
    TLine extends { amount: number; matchedAmount: number },
>({
    total,
    lines = [],
    isMatchableLine,
    isDocumentDiscountLine,
    shouldSubtractDocumentAdjustmentMatches = false,
}: FullyMatchedDocumentInput<TLine>): boolean {
    const matchableLines = lines.filter(isMatchableLine);
    if (matchableLines.length === 0) {
        return false;
    }
    const discountLines = lines.filter(isDocumentDiscountLine);
    if (discountLines.length > 0) {
        const matched = shouldSubtractDocumentAdjustmentMatches
            ? subtractMonetaryValues(
                  sumMonetaryValues(
                      matchableLines.map((line) => line.matchedAmount),
                  ),
                  sumMonetaryValues(
                      discountLines.map((line) => line.matchedAmount),
                  ),
              )
            : sumMonetaryValues(
                  matchableLines.map((line) => line.matchedAmount),
              );
        return fromMonetaryToInt(matched) >= fromMonetaryToInt(total);
    }
    return matchableLines.every(
        (line) => line.matchedAmount >= Math.abs(line.amount),
    );
}

export function getInvoiceOpenAmount(
    invoice: InvoiceOpenAmountInput,
    accrualEntityIds: Set<number>,
): number {
    if (accrualEntityIds.has(invoice.entityId)) {
        if (isClosedInvoiceStatus(invoice.status)) {
            return 0;
        }
        const matched =
            invoice.lines?.reduce((sum, line) => sum + line.matchedAmount, 0) ??
            0;
        return invoice.total - matched;
    }
    return getInvoiceEffectiveStatus(invoice) === InvoiceStatus.OPEN
        ? invoice.total
        : 0;
}

export function getInvoiceLineOpenAmount(
    line: InvoiceLineOpenAmountInput,
    accrualEntityIds: Set<number>,
): number {
    const invoice = line.invoice;
    if (!invoice) {
        return 0;
    }
    if (accrualEntityIds.has(invoice.entityId)) {
        if (isClosedInvoiceStatus(invoice.status)) {
            return 0;
        }
        return line.amount - line.matchedAmount;
    }
    return getInvoiceEffectiveStatus(invoice) === InvoiceStatus.OPEN
        ? line.amount
        : 0;
}

/**
 * Paid amount for the invoices list "Paid" column - the invoice's total when
 * it has reached the PAID status, 0 otherwise. Mirrors the `paid` bucket
 * `buildByCounterpartyAggregatedExpressions` already computes for the
 * by-customer view: a PAID accrual-ledger invoice reads its `status`
 * directly, a non-accrual (or integration) invoice reads its effective
 * status, so a NULL `externalStatus` on a synced record never borrows the
 * clamped internal status.
 */
export function getInvoicePaidAmount(
    invoice: InvoiceOpenAmountInput,
    accrualEntityIds: Set<number>,
): number {
    if (accrualEntityIds.has(invoice.entityId)) {
        return invoice.status === InvoiceStatus.PAID ? invoice.total : 0;
    }
    return getInvoiceEffectiveStatus(invoice) === InvoiceStatus.PAID
        ? invoice.total
        : 0;
}
